import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

import Notice from 'components/Shared/Notice'

import EmptyComponent from './elements/EmptyComponent'
import CarouselDescription from './elements/CarouselDescription'
import ImageList from './elements/ImageList'
import InfoCard from './elements/InfoCard/InfoCard'
import PortalModal from 'components/Shared/PortalModal'
import ExpandedModal from './elements/ExpandedModal'
import { useCarouselAd } from './useCarouselAd'
import { useCarouselWarnings } from './useCarouselWarnings'
import { useCarouselUploadStore } from './useCarouselUploadStore'
import { validateCarouselPlacementConsistency } from './carouselPlacement'
import CoCreate from 'components/Recommendations/Campaign/Edit/CoCreate'
import TipDialogueBox from 'components/Shared/TipDialogueBox'
import { useTranslation } from 'react-i18next'
import ErrorMessage from 'components/Shared/ErrorMessage'
import { LanguageSelector } from 'components/Recommendations/Campaign/Edit/CoCreate'

const CarouselAd = ({
  cards = [],
  onCarouselCardsChange,
  handleChange,
  eventId,
  language,
  onLanguageChange,
  isOpen,
  isRunning,
  readonly,
  bodiesCount,
  form,
  platforms,
  campaignType,
}) => {
  const { t } = useTranslation()
  const {
    carouselData,
    selectedImageId,
    setSelectedImageId,
    expandedImage,
    setExpandedImage,
    addCard,
    handleUpdateCardField,
    handleRemoveCard,
    handleSelectPreviousCard,
    handleSelectNextCard,
    handleOpenExpandedImage,
    handleCoCreateUpdate,
    onBack,
    onNext,
    isFirstItem,
    isLastItem,
  } = useCarouselAd({ cards, onCarouselCardsChange, handleChange, form })
  const uploadStore = useCarouselUploadStore()
  const { completedUploadByCard, consumeCompletedUpload } = uploadStore

  // Reports finished uploads to the form only once React actually runs this
  // effect, using the `cards` prop current *then* — not whatever it was when
  // any given upload started. All cards that finished since the last run are
  // merged into ONE onCarouselCardsChange call (mirrors
  // MediaUpload.service.jsx's bulkUpdateMediaItems batching): merging them
  // one call at a time via handleUpdateCardField would have each call rebuild
  // its "next cards" from the same not-yet-committed base, so two uploads
  // finishing in the same tick would have the second call's result silently
  // clobber the first's.
  useEffect(() => {
    const cardIds = Object.keys(completedUploadByCard)
    if (!cardIds.length) return
    const updatedCards = cards.map((card) =>
      cardIds.includes(card.id) ? { ...card, media: completedUploadByCard[card.id] } : card,
    )
    onCarouselCardsChange(updatedCards)
    cardIds.forEach(consumeCompletedUpload)
  }, [completedUploadByCard, cards, onCarouselCardsChange, consumeCompletedUpload])

  const { groupWarnings, cardWarningsById } = useCarouselWarnings({ cards: carouselData, platforms })
  const mixedPlacementErrorKey = useMemo(
    () => validateCarouselPlacementConsistency(carouselData),
    [carouselData],
  )
  const mixedPlacementMessage = mixedPlacementErrorKey ? t(mixedPlacementErrorKey) : null

  const groupWarningsMessage = (
    <div>
      {groupWarnings.map((warning) => (
        <div className="mb-1" key={warning}>{warning}</div>
      ))}
    </div>
  )

  // hide group warnings for now
  const showGroupWarnings = false

  // return
  return (
    <div>
      {carouselData.length > 0 ? (
        <>
          {!readonly && showGroupWarnings && <Notice
            element={groupWarningsMessage}
            isVisible={groupWarnings.length > 0}
            className="mb-2"
          />}
        
          <ImageList 
            images={carouselData}
            setImages={onCarouselCardsChange}
            selectedImageId={selectedImageId}
            setSelectedImageId={setSelectedImageId}
            addCard={addCard}
            onOpenExpandedImage={handleOpenExpandedImage}
            readonly={readonly}
          />

          {!readonly && <>
            <CarouselDescription cardsCount={carouselData.length} /> 
            
            <TipDialogueBox text={t('Recommendations.campaign.setup.cocreate.description')} />
          </>}

          {readonly && (
            <div className="autofill_card bg-white mb-3">
              <div className="autofill_card__inner p-0">
                <LanguageSelector
                  value={language}
                  chosenLanguages={[]}
                  disabled={readonly}
                  onChange={onLanguageChange}
                />
              </div>
            </div>
          )}

          <CoCreate
            eventId={eventId}
            tc={form?.content?.tc}
            tc_run_id={form?.content?.tc_run_id}
            fields={{
              headlines: carouselData.map((card) => card.headline),
              description: carouselData.map((card) => card.description),
              bodies: Array(bodiesCount).fill(''),
            }}
            onChange={(generatedContent) => handleCoCreateUpdate( generatedContent)}
            language={language}
            onLanguageChanged={onLanguageChange}
            chosenLanguages={[]}
            disabled={readonly}
            className="bg-white"
            platforms={platforms}
            campaignType={campaignType}
          />

          {mixedPlacementMessage && !readonly && <ErrorMessage
            danger
            classNames="mb-3"
          >
            {t(mixedPlacementMessage)}
          </ErrorMessage>}

          <InfoCard 
            images={carouselData} 
            selectedImageId={selectedImageId} 
            setSelectedImageId={setSelectedImageId}
            onChange={onCarouselCardsChange}
            onUpdateCardField={handleUpdateCardField}
            onRemoveCard={handleRemoveCard}
            onSelectPreviousCard={handleSelectPreviousCard}
            onSelectNextCard={handleSelectNextCard}
            onOpenExpandedImage={handleOpenExpandedImage}
            cardWarnings={cardWarningsById[selectedImageId] || []}
            eventId={eventId}
            tc={form?.content?.tc}
            tc_run_id={form?.content?.tc_run_id}
            isOpen={isOpen}
            isRunning={isRunning}
            readonly={readonly}
            uploadStore={uploadStore}
            platforms={platforms}
            campaignType={campaignType}
          />
        </>
      ) : (
        <EmptyComponent addCard={addCard}/>
      )}

      <PortalModal
        isOpen={!!expandedImage}
        className="full-height"
        onClickOutside={() => setExpandedImage(null)}
        title='Media Preview'
      >
        <ExpandedModal 
          expandedImage={expandedImage} 
          onBack={onBack} 
          onNext={onNext} 
          isFirstItem={isFirstItem} 
          isLastItem={isLastItem} />
      </PortalModal>
    </div>
  )
}

export default CarouselAd

CarouselAd.propTypes = {
  cards: PropTypes.array,
  onCarouselCardsChange: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  language: PropTypes.string,
  onLanguageChange: PropTypes.func,
  isOpen: PropTypes.bool,
  isRunning: PropTypes.bool,
  form: PropTypes.object,
  platforms: PropTypes.array,
  campaignType: PropTypes.string,
}